<script setup lang="ts">
import { ShieldX } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

import AiModelCatalogTable from '@/components/settingsMenu/sections/AiModelCatalogTable.vue';
import {
    type AiModel,
    getAiModels,
    setModelAllowed
} from '@/services/ai_models';
import { useModelStore } from '@/stores/modelStore';
import { useTenantStore } from '@/stores/tenant';

const tenantStore = useTenantStore();
const modelStore = useModelStore();

const models = ref<AiModel[]>([]);
const loading = ref(false);
const savingIds = ref(new Set<string>());
const error = ref<string | null>(null);

const isAdmin = computed(() => tenantStore.tenantAdmin);
const tenantId = computed(() => tenantStore.tenantId || '');

async function toggleModel(model: AiModel, enabled: boolean) {
    savingIds.value.add(model.model_id);
    error.value = null;
    try {
        await setModelAllowed(tenantId.value, model.model_id, enabled);
        model.allowed = enabled;
        await modelStore.loadAllowedModels();
    } catch (err) {
        console.error(err);
        error.value = 'Failed to update model selection.';
    } finally {
        savingIds.value.delete(model.model_id);
    }
}

async function loadData() {
    const tid = tenantId.value;
    if (!tid || !isAdmin.value) return;

    loading.value = true;
    error.value = null;
    try {
        models.value = await getAiModels(tid);
    } catch (err) {
        console.error(err);
        error.value = 'Failed to load AI models.';
    } finally {
        loading.value = false;
    }
}

watch(
    () => [tenantStore.tenantId, tenantStore.tenantAdmin],
    () => {
        loadData();
    },
    { immediate: true }
);
</script>

<template>
    <div class="flex flex-col gap-6">
        <div>
            <h2 class="text-xl font-semibold">AI Models</h2>
            <p class="text-sm text-muted-foreground mt-1">
                Choose which AI models are available to users in your workspace.
            </p>
        </div>

        <div
            v-if="!isAdmin"
            class="border rounded-xl p-6 flex items-center gap-3 text-muted-foreground"
        >
            <ShieldX class="h-5 w-5" />
            This section is available only to workspace admins.
        </div>

        <div
            v-else-if="error"
            class="p-3 bg-destructive/10 border border-destructive/20 rounded-lg"
        >
            <p class="text-sm text-destructive">{{ error }}</p>
        </div>

        <AiModelCatalogTable
            v-if="isAdmin"
            :models="models"
            :loading="loading"
            :saving-ids="savingIds"
            @toggle-model="toggleModel"
        />
    </div>
</template>
